/**
 * Project switcher at the top of the left rail.
 *
 * Renders the active project (avatar, name, status pill, chevron) in its resting
 * state. When opened, displays a dropdown with an 'All projects' link at the top,
 * the list of the user's projects with their status, and a 'New project' action.
 *
 * Switching projects preserves the active sub-route where applicable (e.g.
 * /p/tetris/database -> /p/herb-and-ash/database).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useProjects } from '../../lib/auth/use-app-data';
import { StatusPill } from '../patterns/StatusPill';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import type { Project } from '../../lib/types';
import '../../styles/components/project-switcher.css';

interface ProjectSwitcherProps {
  slug: string | null;
}

export function ProjectSwitcher({ slug }: ProjectSwitcherProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const projects = useProjects();
  const current = slug ? projects.find((p) => p.slug === slug) : undefined;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(
    (returnFocus = true) => {
      setOpen(false);
      setSearch('');
      if (returnFocus) buttonRef.current?.focus();
    },
    [],
  );

  // Click outside listener
  useEffect(() => {
    if (!open) return;
    function onDown(event: MouseEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) {
        close(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  // Focus management on open
  useEffect(() => {
    if (!open) return;
    captureEvent('project_switcher_opened', { current_slug: slug });
    const firstButton = menuRef.current?.querySelector<HTMLButtonElement>('button');
    firstButton?.focus();
  }, [open, slug]);

  // Keyboard navigation
  const onMenuKey = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      const nodes = Array.from(
        menuRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? [],
      );
      if (nodes.length === 0) return;
      const at = nodes.indexOf(document.activeElement as HTMLButtonElement);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = (at + step + nodes.length) % nodes.length;
      nodes[next].focus();
    },
    [close],
  );

  function goToAllProjects() {
    captureEvent('project_switcher_all_projects_clicked');
    close(false);
    navigate('/projects');
  }

  function goToNewProject() {
    captureEvent('project_switcher_new_project_clicked');
    close(false);
    navigate('/new');
  }

  function onSelectProject(project: Project) {
    captureEvent('project_switcher_project_selected', {
      from_slug: slug,
      to_slug: project.slug,
      project_id: project.id,
    });
    close(false);
    if (slug) {
      const currentPrefix = `/p/${encodeURIComponent(slug)}`;
      if (location.pathname.startsWith(currentPrefix)) {
        const subPath = location.pathname.slice(currentPrefix.length);
        navigate(`/p/${encodeURIComponent(project.slug)}${subPath}`);
        return;
      }
    }
    navigate(`/p/${encodeURIComponent(project.slug)}`);
  }

  // The menu lists the account's APPS. A staging environment is an environment
  // of one, reached from that app's settings, and listing it here would offer
  // every opted-in app twice. The button above still names whichever of the two
  // you are actually looking at, which is the part that has to be right.
  const switchable = projects.filter((p) => p.environment !== 'staging');
  const query = search.trim().toLowerCase();
  const filteredProjects = query
    ? switchable.filter(
        (p) => p.name.toLowerCase().includes(query) || p.slug.toLowerCase().includes(query),
      )
    : switchable;

  return (
    <div className="switcher-wrap" ref={wrapRef}>
      <button
        type="button"
        ref={buttonRef}
        className="switcher"
        data-tour="project-switcher"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={current ? t('Switch project: {name}', { name: current.name }) : t('Project switcher')}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="switcher__avatar" aria-hidden="true">
          {current ? current.name.slice(0, 1) : null}
        </span>
        <span className="switcher__name" title={current ? current.name : t('All projects')}>
          {current ? current.name : t('All projects')}
        </span>
        {/* Which environment you are looking at, when it is not the live one.
            The row's name already ends in "(staging)", but the cost of acting
            on the wrong environment is high enough that it should not depend on
            reading to the end of a truncated name. */}
        {current?.environment === 'staging' ? (
          <span className="switcher__env">{t('Staging')}</span>
        ) : null}
        {current ? (
          <span className="switcher__status">
            <StatusPill value={current.status} size="sm" />
          </span>
        ) : null}
        <span
          className={`switcher__chev ${open ? 'switcher__chev--open' : ''}`}
          aria-hidden="true"
        >
          ▾
        </span>
      </button>

      {open ? (
        <div
          className="switcher-menu"
          role="menu"
          aria-label={t('Projects')}
          ref={menuRef}
          onKeyDown={onMenuKey}
        >
          <button
            type="button"
            role="menuitem"
            className={`switcher-menu__item switcher-menu__item--all ${!slug ? 'switcher-menu__item--active' : ''}`}
            onClick={goToAllProjects}
          >
            <span className="switcher-menu__all-icon" aria-hidden="true">
              <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.4">
                <rect x="2.5" y="2.5" width="4.5" height="4.5" rx="1" />
                <rect x="9" y="2.5" width="4.5" height="4.5" rx="1" />
                <rect x="2.5" y="9" width="4.5" height="4.5" rx="1" />
                <rect x="9" y="9" width="4.5" height="4.5" rx="1" />
              </svg>
            </span>
            <span className="switcher-menu__item-name">{t('All projects')}</span>
            {!slug ? (
              <span className="switcher-menu__check" aria-hidden="true">
                ✓
              </span>
            ) : null}
          </button>

          <div className="switcher-menu__rule" role="presentation" />

          {switchable.length > 5 ? (
            <div className="switcher-menu__search">
              <input
                type="text"
                className="switcher-menu__input"
                placeholder={t('Filter projects...')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                aria-label={t('Filter projects')}
              />
            </div>
          ) : null}

          <div className="switcher-menu__list" role="group" aria-label={t('Projects')}>
            {filteredProjects.map((project) => {
              const isSelected = project.slug === slug;
              return (
                <button
                  type="button"
                  role="menuitem"
                  key={project.id}
                  className={`switcher-menu__item ${isSelected ? 'switcher-menu__item--active' : ''}`}
                  aria-current={isSelected ? 'true' : undefined}
                  onClick={() => onSelectProject(project)}
                >
                  <span className="switcher-menu__avatar" aria-hidden="true">
                    {project.name.slice(0, 1)}
                  </span>
                  <span className="switcher-menu__item-name" title={project.name}>
                    {project.name}
                  </span>
                  <span className="switcher-menu__status">
                    <StatusPill value={project.status} size="sm" />
                  </span>
                  {isSelected ? (
                    <span className="switcher-menu__check" aria-hidden="true">
                      ✓
                    </span>
                  ) : null}
                </button>
              );
            })}
            {filteredProjects.length === 0 ? (
              <div className="switcher-menu__empty">{t('No projects found')}</div>
            ) : null}
          </div>

          <div className="switcher-menu__rule" role="presentation" />

          <button
            type="button"
            role="menuitem"
            className="switcher-menu__item switcher-menu__item--new"
            onClick={goToNewProject}
          >
            <span className="switcher-menu__plus" aria-hidden="true">
              +
            </span>
            <span className="switcher-menu__item-name">{t('New project')}</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
